import PublicPresentationGallery from "./PublicPresentationGallery";
import PublicPresentationMap from "./PublicPresentationMap";
import PublicPresentationContactCard from "./PublicPresentationContactCard";
import { pickPublicProjection } from "../lib/publicProjection";
import {
  DEAL_TYPE_LABELS,
  PROPERTY_TYPE_LABELS,
  formatLocationLine,
  formatResolvedPrice,
} from "../lib/labels";
import { factFieldLabel, formatFactValue, UNKNOWN_ENUM_LABEL } from "../../realty/lib/specLabels";
import { presentationFactIcon } from "../lib/factIcons";

const HEADLINE_KEYS = {
  residential: ["total_area_m2", "rooms", "floor", "bathrooms"],
  land: ["area_m2", "purpose"],
  commercial: ["total_area_m2", "floor", "suitable_for"],
};

function subtypeFacts(facts) {
  if (!facts || typeof facts !== "object") return { kind: null, values: {} };
  if (facts.residential && typeof facts.residential === "object") {
    return { kind: "residential", values: facts.residential };
  }
  if (facts.land && typeof facts.land === "object") {
    return { kind: "land", values: facts.land };
  }
  if (facts.commercial && typeof facts.commercial === "object") {
    return { kind: "commercial", values: facts.commercial };
  }
  return { kind: null, values: {} };
}

function visibleEntries(values) {
  return Object.entries(values).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
}

function PublicPresentationRenderer({
  presentation,
  onPhoneActivate,
  onEmailActivate,
  footer = true,
  variant = "public",
}) {
  const data = pickPublicProjection(presentation);
  if (!data) return null;

  const typeLabel = PROPERTY_TYPE_LABELS[data.property_type]
    || (data.property_type ? UNKNOWN_ENUM_LABEL : "");
  const dealLabel = DEAL_TYPE_LABELS[data.deal_type]
    || (data.deal_type ? UNKNOWN_ENUM_LABEL : "");
  const locationLine = formatLocationLine(data.location);
  const priceText = formatResolvedPrice(data.price);
  const { kind, values } = subtypeFacts(data.facts);
  const headlineKeys = HEADLINE_KEYS[kind] || [];
  const headline = headlineKeys
    .map((key) => {
      const formatted = formatFactValue(key, values[key], values);
      const label = factFieldLabel(key);
      if (!formatted || !label) return null;
      return { key, label, value: formatted, icon: presentationFactIcon(key) };
    })
    .filter(Boolean);
  const headlineKeySet = new Set(headline.map((item) => item.key));
  if (headlineKeySet.has("floor")) headlineKeySet.add("total_floors");
  const details = visibleEntries(values)
    .filter(([key]) => !headlineKeySet.has(key))
    .map(([key, value]) => {
      const formatted = formatFactValue(key, value, values);
      const label = factFieldLabel(key);
      if (!formatted || !label) return null;
      return { key, label, value: formatted };
    })
    .filter(Boolean);
  const features = Array.isArray(data.features) ? data.features : [];
  const videoUrl = data.video_url || null;
  const ownerPreview = variant === "ownerPreview";

  return (
    <article
      className={[
        "presentation-renderer",
        ownerPreview ? "presentation-renderer--owner-preview" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid="presentation-renderer"
      data-variant={variant}
    >
      <PublicPresentationGallery media={data.media} />
      <header className="presentation-heading">
        <h1 className="presentation-title">{data.title || "Об'єкт"}</h1>
        <p className="presentation-kicker">
          {[typeLabel, dealLabel, locationLine].filter(Boolean).join(" · ")}
        </p>
        <p className="presentation-price">{priceText}</p>
      </header>
      {headline.length ? (
        <ul className="presentation-headline" aria-label="Ключові характеристики">
          {headline.map((fact) => (
            <li key={fact.key} data-fact-key={fact.key}>
              <i className={fact.icon} aria-hidden="true" />
              <span className="presentation-headline__copy">
                <span className="presentation-headline__label">{fact.label}</span>
                <strong className="presentation-headline__value">{fact.value}</strong>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {details.length ? (
        <section className="presentation-facts" aria-labelledby="presentation-facts-heading">
          <h2 id="presentation-facts-heading" className="presentation-section-title">
            <i className="fa-solid fa-list" aria-hidden="true" />
            Характеристики
          </h2>
          <dl className="presentation-specs" data-testid="presentation-facts-rows">
            {details.map((fact) => (
              <div key={fact.key} className="presentation-specs__row">
                <dt className="presentation-facts__label">{fact.label}</dt>
                <dd className="presentation-facts__value">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      {data.description_public ? (
        <section className="presentation-copy" aria-labelledby="presentation-desc-heading">
          <h2 id="presentation-desc-heading" className="presentation-section-title">
            <i className="fa-solid fa-align-left" aria-hidden="true" />
            Опис
          </h2>
          <p className="presentation-copy__text">{data.description_public}</p>
        </section>
      ) : null}
      {data.presentation_note ? (
        <section className="presentation-note" aria-labelledby="presentation-note-heading">
          <h2 id="presentation-note-heading" className="presentation-section-title">
            <i className="fa-solid fa-comment" aria-hidden="true" />
            Коментар для вас
          </h2>
          <p className="presentation-copy__text">{data.presentation_note}</p>
        </section>
      ) : null}
      {features.length ? (
        <section className="presentation-features" aria-labelledby="presentation-features-heading">
          <h2 id="presentation-features-heading" className="presentation-section-title">
            <i className="fa-solid fa-check" aria-hidden="true" />
            Зручності
          </h2>
          <ul>
            {features.map((feature, index) => (
              <li key={`${feature.name || "feature"}-${index}`}>
                {feature.icon ? <i className={feature.icon} aria-hidden="true" /> : null}
                <span>{feature.name}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {videoUrl ? (
        <section className="presentation-video" aria-labelledby="presentation-video-heading">
          <h2 id="presentation-video-heading" className="presentation-section-title">
            <i className="fa-solid fa-play" aria-hidden="true" />
            Відео
          </h2>
          <iframe
            src={videoUrl}
            title="Відео презентації"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="no-referrer"
            data-testid="presentation-video"
          />
        </section>
      ) : null}
      <PublicPresentationMap location={data.location} />
      <PublicPresentationContactCard
        contact={data.contact}
        onPhoneActivate={onPhoneActivate}
        onEmailActivate={onEmailActivate}
      />
      {footer ? (
        <footer className="presentation-footer">
          <p>Презентація підготовлена в Рієстро</p>
        </footer>
      ) : null}
    </article>
  );
}

export default PublicPresentationRenderer;
